<template>
  <d-card class="mb-4">
    <table class="table mb-0">
      <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0 d-flex">
            <span>{{ $t('product.productName') }}</span>
          </th>
          <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.amount') }}</th>
          <th scope="col" class="border-0" style="width: 180px;">{{ $t('price.pricePerItem') }}</th>
          <th scope="col" class="border-0" style="width: 150px;">{{ $t('order.totalCost') }}</th>
          <th scope="col" class="border-0" style="width: 80px;" v-if="!readOnly">{{ $t('ui.delete') }}</th>
          <th scope="col" class="border-0" style="width: 200px;" v-if="isOrderView">{{ $t('ui.move') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="align-top d-flex is-v-select-wrapper flex-wrap">
            <div class="d-flex w-75 product-name">
              <img
                v-if="getProductImageById(productData.product_id)"
                :src="getProductImageById(productData.product_id)"
                alt=""
                class="product-image" />
              <span
                v-else
                class="product-empty-image">
                <i class="material-icons">camera_alt</i>
              </span>

              <validation-provider
                v-if="!readOnly"
                rules="required"
                :name="'product-id-' + productIndex"
                tag="div"
                class="w-100"
                v-slot="{ errors }">
                <div :class="{ 'form-control w-100': true, 'is-select': true, 'is-invalid': errors.length }">
                  <v-select
                    :options="products"
                    v-model="productData.product_id"
                    @input="updateProductPrice($event);"
                    :reduce="product => product.id"
                    :filterBy="filterByForProducts"
                    :placeholder="$t('product.selectProduct')"
                    label="code">
                    <template slot="no-options">
                      {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center">
                        {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center">
                        {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
              </validation-provider>
              <span v-else>
                {{ getProductNameById(productData.product_id) }}
              </span>
            </div>
            <div class="w-25">
              <d-button
                v-if="!readOnly"
                class="w-100 ml-2"
                outline
                @click.prevent="$bus.$emit('products-search-popup-open', productIndex)">
                <i class="material-icons">search</i>
                {{ $t('ui.search') }}
              </d-button>
            </div>
          </td>
          <td class="align-top">
            <validation-provider
              v-if="!readOnly"
              rules="required|min_value:1"
              :name="'product-amount-' + productIndex"
              v-slot="{ errors }">
              <d-input-group>
                <d-input
                  :min="1"
                  :state="errors.length ? 'invalid' : null"
                  v-model="productData.quantity"
                  type="number" />
                <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
              </d-input-group>
            </validation-provider>
            <template v-else>
              {{ productData.quantity }} {{ $t('ui.piecesAbbr') }}<br>
            </template>

            <template v-if="this.productData.product_id">
              <small v-if="selectedProductAvailability !== false" class="d-block">
                <span :class="quantityToCompare < productData.quantity ? 'text-danger' : 'text-primary'">
                  {{ $t('ui.availability') }}: {{ quantityToCompare }} {{ $t('ui.piecesAbbr') }}
                </span>
              </small>
              <small v-else class="d-block">
                {{ $t('ui.noAvailabilityData') }}
              </small>
            </template>
          </td>
          <td class="align-top">
            <div>{{ Number(productData.price).toFixed(2) }} {{ selectedCurrencySymbol }}</div>
            <del
              v-if="productData.price !== productData.price_before_discount"
              style="display: block; line-height: 1;">
              <small>
                {{ Number(productData.price_before_discount).toFixed(2) }} {{ selectedCurrencySymbol }}
              </small>
            </del>

            <small 
              v-if="!productData.include_discounts"
              class="text-danger d-block">
              {{ $t('product.notIncludedInDiscounts') }}
            </small>
          </td>
          <td class="align-top">
            <template v-if="productData.totalPrice && !Number.isNaN(productData.totalPrice)">
              <span class="text-primary">
                {{ productData.totalPrice }} {{ selectedCurrencySymbol }}
              </span>
            </template>
          </td>
          <td
            v-if="!readOnly"
            class="align-top">
            <d-button
              outline
              @click.prevent="removeProduct()"
              theme="danger">
              {{ $t('ui.delete') }}
            </d-button>
          </td>
          <td
            v-if="isOrderView"
            class="align-top">
            <d-button
              outline
              @click.prevent="moveToOffer()"
              theme="success">
              {{ $t('product.moveToOffer') }}
            </d-button>
          </td>
        </tr>

        <tr v-if="productData.hasDescription || productData.customer_description">
          <td colspan="5">
            <d-form-textarea
              v-if="!readOnly"
              :placeholder="$t('product.enterAdditionalProductDescriptionOptional')"
              :rows="3"
              :max-rows="6"
              :value.prop="productData.customer_description"
              v-model="productData.customer_description"></d-form-textarea>
            <template v-else>
              <em class="text-light">{{ productData.customer_description }}</em>
            </template>
          </td>
        </tr>

        <tr v-if="!readOnly && !productData.hasDescription">
          <td colspan="5">
            <a
              v-if="!productData.hasDescription"
              @click.prevent="productData.hasDescription = true;"
              href="#add-desc"
              class="mr-3">
              <small>
                <i class="material-icons">add</i> {{ $t('ui.addDescription') }}
              </small>
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </d-card>
</template>

<script>
import CustomDecimals from './../../utils/CustomDecimals.js';

export default {
  name: 'request-creator-product',
  props: [
    'currencyId',
    'readOnly',
    'productData',
    'productIndex',
    'products',
    'productsDiscount',
    'selectedCurrencySymbol',
    'isOrderView'
  ],
  computed: {
    selectedProductAvailability () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.product_id);

      if (foundedProduct && foundedProduct.length) {
        if (!foundedProduct[0].availability) {
          return 0;
        }

        return parseInt(foundedProduct[0].availability, 10);
      }

      return false;
    },
    quantityToCompare () {
      return this.selectedProductAvailability;
    }
  },
  watch: {
    'productData.product_id': function (newValue) {
      this.updateProductPrice(newValue);
    }
  },
  methods: {
    filterByForProducts (option, label, search) {
      return (option.code + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    removeProduct () {
      this.$bus.$emit('request-creator-remove-product', this.productIndex);
    },
    updateProductPrice (productID) {
      let productItem = this.products.filter(product => product.id === productID)[0];

      if (!productItem) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        this.$parent.productsData[this.productIndex].price_before_discount = '0.00';
        return;
      }

      this.$parent.productsData[this.productIndex].productData = JSON.parse(JSON.stringify(productItem));
      this.$parent.productsData[this.productIndex].include_discounts = productItem.include_discounts;
      let productPrice = productItem.prices.filter(price => price.currency_id === this.currencyId)[0];

      if (!productPrice) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        this.$parent.productsData[this.productIndex].price_before_discount = '0.00';
        return;
      }

      this.$parent.productsData[this.productIndex].price_before_discount = CustomDecimals.roundProperly(parseFloat(productPrice.price));

      if (this.productsDiscount > 0 && this.$parent.productsData[this.productIndex].include_discounts) {
        this.$parent.productsData[this.productIndex].price = CustomDecimals.roundProperly(parseFloat(productPrice.price) - ((this.productsDiscount / 100) * parseFloat(productPrice.price)));
      } else {
        this.$parent.productsData[this.productIndex].price = CustomDecimals.roundProperly(parseFloat(productPrice.price));
      }
    },
    getProductNameById (id) {
      let foundedProduct = this.products.filter(product => product.id === id, 10);

      if (foundedProduct.length) {
        return foundedProduct[0].name;
      }

      return '';
    },
    getProductImageById (id) {
      let foundedProduct = this.products.filter(product => product.id === id, 10);

      if (foundedProduct.length) {
        return foundedProduct[0].image_url;
      }

      return '';
    },
    moveToOffer () {
      this.$bus.$emit('order-creator-move-product-from-inquiry', this.productData.product_id, this.productData.quantity);
    }
  }
}
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}

.product-name {
  justify-content: flex-start;
}

.product {
  &-image {
    height: auto;
    margin: 0 20px 0 0;
    width: 120px;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 120px;
    justify-content: center;
    margin: 0 20px 0 0;
    width: 120px;
  }
}
</style>
